import { isEdited, type Field } from '@sonder/shared'
import { createContext, useCallback, useContext, type ReactNode } from 'react'
import { useAuth } from '@/auth/AuthProvider'
import type { ReviewInfo } from '@/ui'
import { useMembers } from './client'

type NameOf = (uid: string | undefined) => string | undefined
const Ctx = createContext<NameOf>(() => undefined)

/**
 * Names the person behind a confirmation or edit: "you", a client member by name, otherwise Sonder Media House
 * (only the team and the client's people can write). Nothing until the member list loads, so a name never flickers.
 */
export function PeopleNamesProvider({ clientId, children }: { clientId: string; children: ReactNode }) {
  const members = useMembers(clientId)
  const { user } = useAuth()
  const nameOf = useCallback<NameOf>(
    (uid) => {
      if (!uid || uid.startsWith('import:') || uid === 'system') return undefined
      if (uid === user?.uid) return 'you'
      if (members.loading) return undefined
      const m = members.items.find((x) => x.id === uid)
      return m ? m.name || m.email : 'Sonder Media House'
    },
    [members.items, members.loading, user?.uid],
  )
  return <Ctx.Provider value={nameOf}>{children}</Ctx.Provider>
}

export function useNameOf(): NameOf {
  return useContext(Ctx)
}

const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

/** "Confirmed by Maria · Oct 2" or "Edited by you · Oct 2"; nothing for a value still waiting on the client. */
export function reviewByline(f: Field<unknown> | undefined, nameOf: NameOf): string | undefined {
  if (!f?.prefilled) return undefined
  const line = (verb: string, uid: string, at: number) => {
    const who = nameOf(uid)
    return who ? `${verb} by ${who} · ${day(at)}` : `${verb} ${day(at)}`
  }
  if (isEdited(f)) return line('Edited', f.updatedBy, f.updatedAt)
  if (f.confirmed) return line('Confirmed', f.confirmedBy ?? f.updatedBy, f.confirmedAt ?? f.updatedAt)
  return undefined
}

/** The client-view review state of one pre-filled value, or undefined when there is nothing to review. */
export function reviewInfo(
  f: Field<unknown> | undefined,
  nameOf: NameOf,
  { confirmable = true, onConfirm, onUndo }: { confirmable?: boolean; onConfirm?: (next: boolean) => void; onUndo?: () => void },
): ReviewInfo | undefined {
  if (!f?.prefilled) return undefined
  const state: ReviewInfo['state'] = isEdited(f) ? 'edited' : f.confirmed ? 'confirmed' : 'pending'
  return { state, confirmable, onConfirm, onUndo: f.original ? onUndo : undefined, byline: reviewByline(f, nameOf) }
}
